import { defineStore } from 'pinia'
import { getMailboxConnections } from '@/api/v2/mailbox_connections'
import type { MailboxConnection, MailScope } from '@/models/MailboxConnection'

interface State {
  connections: Record<MailScope, MailboxConnection[]>
  loading: boolean
  openMessageId: string | null
}

/** Mailbox connections cache per scope plus the id of the message shown in the detail drawer. */
export const useMailStore = defineStore('mail', {
  state: (): State => ({ connections: { personal: [], shared: [] }, loading: false, openMessageId: null }),

  actions: {
    async refreshConnections(scope: MailScope): Promise<MailboxConnection[]> {
      const session = epoch
      this.loading = true
      try {
        const resp = await getMailboxConnections(scope)
        // The session ended meanwhile (logout, account switch): the list is the previous user's.
        if (session === epoch) this.connections[scope] = resp.data.data
      } finally {
        if (session === epoch) this.loading = false
      }
      return this.connections[scope]
    },

    /** Replaces the connection in its scope list (by id) or appends it. */
    upsertConnection(connection: MailboxConnection) {
      const list = this.connections[connection.is_shared ? 'shared' : 'personal']
      const idx = list.findIndex((c) => c.id === connection.id)
      if (idx >= 0) list[idx] = connection
      else list.push(connection)
    },

    removeConnection(id: string) {
      for (const scope of ['personal', 'shared'] as const) {
        this.connections[scope] = this.connections[scope].filter((c) => c.id !== id)
      }
    },

    setOpenMessage(id: string | null) {
      this.openMessageId = id
    },
  },
})

/** Bumped when a session ends; mailbox lists still in flight then belong to the previous one. */
let epoch = 0

/** Drops answers of requests started before (logout, account switch; see stores/mailSession.ts). */
export function endMailSession(): void {
  epoch++
}
